import { MantineReactTable, useMantineReactTable } from 'mantine-react-table';
import { Button, Select, Menu, Modal } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Typography } from '@mui/material';
import { faEye, faEllipsisV, faDownload, faLaptop } from '@fortawesome/free-solid-svg-icons';
import { faWindows, faLinux } from '@fortawesome/free-brands-svg-icons';
import { useNavigate } from 'react-router-dom';
import { useState, useEffect, useContext, useRef } from 'react';
import { getUSBstatus, blockUSB, formatNonOSDrives } from '../../apis/agent';
import { editLastLogin, addOperation, addQueueOperation, editUsbStatus, requestOperation, checkValid } from '../../apis/axios';
import ConformationDialog from '../common/ConfirmationModal';
import ShowLocation from './ShowLocation'
import AuthContext from '../../AuthContext';
import { toast } from 'react-toastify'
import {
  useTheme, Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material'
import Table from '../common/CommonTable'
// Columns definition
const columns = (auth, handleUSBAction, handleUnlicencedDeviceClick, handleEraseWithoutOS, handleNukeOS, loadingDevices, handleInactiveDeviceClick, handleOpenLocation) => [
  {
    accessorKey: 'name',
    header: 'Device Name',
    size: 180,
    minSize: 120,
    Cell: ({ cell, row }) => {
      const deviceName = cell.getValue();
      const userType = auth?.userType;
      const isSuperAdmin = userType === "superadmin"
      const navigate = useNavigate();
      const isActive = parseInt(row.original.isActive) === 1;
      const isLicenced = parseInt(row.original.licenced) === 1; // Check licenced status
      return (
        <div
          className="flex items-center space-x-2 cursor-pointer group"
          onClick={() => {
           if (isSuperAdmin) return;
            if (isActive && isLicenced) {
              navigate(`/drive-details/${deviceName}`, { state: { device: row.original } });
            } else if (!isLicenced) {
              handleUnlicencedDeviceClick(); // Trigger unlicensed modalelse
            }
            else if (!isActive) {
              handleInactiveDeviceClick();
            }
            else {
              navigate(`/drive-details/${deviceName}`, { state: { device: row.original } });
            }
          }}
        >
          <FontAwesomeIcon icon={faLaptop} className="text-[#595C60] group-hover:text-[#A855F7]" />
          <span className="text-[#595C60] group-hover:text-[#A855F7]">
            {deviceName}
          </span>
        </div>
      );
    },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'ip_address',
    header: 'Location',
    size: 150,
    minSize: 120,
    Cell: ({ row }) => {
      const ip = row.original.ip_address;
      return (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleOpenLocation(ip)}
          sx={{
            textTransform: "none",
            borderRadius: "20px",
            padding: "4px 12px",
            fontSize: "12px",
            fontWeight: 600,
            color: "#A855F7",
            borderColor: "#A855F7",
            '&:hover': {
              backgroundColor: "#F3E8FF",
              borderColor: "#A855F7"
            }
          }}
        >
          View Location
        </Button>
      );
    }
  },
  {
    accessorKey: 'isActive',
    header: 'Status',
    size: 150,
    minSize: 100,
    Cell: ({ cell }) => {
      const status = cell.getValue();
      const displayStatus = parseInt(status) === 1 ? 'Active' : 'Inactive';
      const colors = {
        Active: { background: '#A855F7', text: '#FFF3E5' },
        Inactive: { background: '#A0AEC0', text: '#EDF2F7' },
      };
      const { background, text } = colors[displayStatus];
      return (
        <span
          style={{
            backgroundColor: background,
            color: text,
            width: '94px',
            height: '28px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '9999px',
            fontSize: '14px',
            fontWeight: 500,
          }}
        >
          {displayStatus}
        </span>
      );
    },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
      },
    },
  },
  {
    accessorKey: 'department_name',
    header: 'Department',
    size: 180,
    minSize: 120,
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'os',
    header: 'OS',
    size: 150,
    minSize: 100,
    Cell: ({ cell }) => {
      const osName = cell.getValue();
      const isWindows = osName.toLowerCase().includes('windows');
      const icon = isWindows ? faWindows : faLinux;
      const iconColor = isWindows ? '#00B0FF' : '#202224';
      return (
        <div className="flex items-center space-x-2">
          <FontAwesomeIcon icon={icon} size="sm" style={{ color: iconColor }} />
          <span>{osName}</span>
        </div>
      );
    },
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'model',
    header: 'Model',
    size: 150,
    minSize: 120,
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'location',
    header: 'Location',
    size: 150,
    minSize: 120,
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'disks',
    header: 'Disks',
    size: 120,
    minSize: 80,
    mantineTableBodyCellProps: {
      style: {
        fontFamily: 'Nunito Sans, sans-serif',
        fontWeight: 600,
        color: '#595C60',
      },
    },
  },
  {
    accessorKey: 'actions',
    header: 'Actions',
    size: 100,
    minSize: 80,
    Cell: ({ row }) => {
      const navigate = useNavigate();
      const deviceUUID = row.original.uuid;
      const usbStatus = row.original.usb_status || 'unknown';
      const usbActionLabel = usbStatus === 'block' ? 'Unblock USB' : 'Block USB';
      const isLoading = loadingDevices[deviceUUID] || false;
      const isActive = row.original.isActive === 1;
      const { deletion, enc_dec, usb, expired } = row.original;
      const userType = auth?.userType;
const isSuperAdmin = userType === "superadmin"

      return (
        <div className="flex justify-start items-center space-x-3 relative" style={{ backgroundColor: 'transparent' }}>
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="spinner"></div>
            </div>
          )}

          <FontAwesomeIcon
            icon={faEye}
            size="lg"
            className={`text-[#A855F7] cursor-pointer hover:text-[#e68f3b] ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
            title="View"
            onClick={() => {
              if (isSuperAdmin) return; // Prevent navigation for superadmin
              if (isActive) {
                navigate(`/drive-details/${row.original.name}`, { state: { device: row.original } });
              } else {
                handleInactiveDeviceClick();
              }
            }}
          />
         {auth.userType!=='superadmin' && (<>
          <Menu
            shadow="md"
            width={200}
            position="bottom-end"
            withinPortal={true}
            zIndex={1000}
            styles={{
              dropdown: {
                backgroundColor: '#ffffff',
                zIndex: 1000,
                position: 'absolute',
              },
              item: {
                backgroundColor: 'transparent',
                color: '#595C60',
                '&[data-hovered]': {
                  backgroundColor: '#FFF3E5',
                  color: '#595C60',
                },
                '&:hover': {
                  backgroundColor: '#FFF3E5',
                  color: '#595C60',
                },
              },
            }}
          >
            <Menu.Target>
              <FontAwesomeIcon
                icon={faEllipsisV}
                size="lg"
                className={`text-[#A855F7] cursor-pointer hover:text-[#e68f3b] ${isLoading ? 'opacity-50 pointer-events-none' : ''}`}
                title="More"
              />
            </Menu.Target>


            <Menu.Dropdown>
              {/* If license expired → show nothing */}
              {expired === 1 && (
                <Menu.Item disabled>
                  License Expired
                </Menu.Item>
              )}

              {expired !== 1 && deletion === 1 &&
                (<>
                  <Menu.Item
                    onClick={() => handleNukeOS(row.original)}
                    disabled={isLoading}
                  >
                    Nuke OS
                  </Menu.Item>
                  <Menu.Item
                    onClick={() => handleEraseWithoutOS(row.original)}
                    disabled={isLoading}
                  >
                    Wipe w/o OS
                  </Menu.Item>
                </>)
              }
              {
                expired !== 1 && usb === 1 && (<Menu.Item
                  onClick={() => handleUSBAction(row.original, usbStatus)}
                  disabled={isLoading}
                >
                  {usbActionLabel}
                </Menu.Item>)
              }

            </Menu.Dropdown>
          </Menu>
         </>)}
        </div>
      );
    },
    mantineTableBodyCellProps: {
      style: {
        height: '48px',
        backgroundColor: 'transparent !important',
        position: 'relative',
        zIndex: 1,
      },
    },
  },
];

// Function to export data as CSV
const exportToCSV = (data, filename) => {
  if (!Array.isArray(data)) {
    console.warn('exportToCSV: Data is not an array, defaulting to empty array.');
    return;
  }

  const headers = [
    'Device Name',
    'Status',
    'Department',
    'OS',
    'Model',
    'Location',
    'Disks',
  ];
  const rows = data.map(item => [
    item.name,
    item.isActive === 1 ? 'Active' : 'Inactive',
    item.department_name,
    item.os,
    item.model,
    item.location,
    item.disks,
  ]);

  const csvContent = [
    headers.join(','),
    ...rows.map(row => row.join(',')),
  ].join('\n');

  const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);
  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.click();
  URL.revokeObjectURL(url);
};

const DeviceTable = ({ data, handleUpdate }) => {
  const auth = useContext(AuthContext);
  const theme = useTheme()
  const [locationIp, setLocationIp] = useState(null);
  const [locationOpen, setLocationOpen] = useState(false);

  const [usbStatuses, setUsbStatuses] = useState({});
  const [loadingDevices, setLoadingDevices] = useState({});
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isUnlicencedModalOpen, setIsUnlicencedModalOpen] = useState(false); // New state for unlicensed modal
  const [liveStatuses, setLiveStatuses] = useState({});
  const [modalConfig, setModalConfig] = useState({
    title: '',
    message: '',
    onConfirm: () => { },
    deviceUUIDs: [],
    actionType: '',
  });
  const handleOpenLocation = (data) => {
    setLocationOpen(true)
    setLocationIp(data)
  }
  const handleCloseLocation = () => {
    setLocationOpen(false)
    setLocationIp(null)
  }
  const [isInactiveModalOpen, setIsInactiveModalOpen] = useState(false);
  const previousActivityStatusRef = useRef({}); // Use ref to track previous activity status
  const handleUnlicencedDeviceClick = () => {
    setIsUnlicencedModalOpen(true);
  };
  // Initialize previous activity status
  useEffect(() => {
    if (data && data.length > 0) {
      const updatedActivityStatus = data.reduce((acc, device) => {
        acc[device.uuid] = device.isActive === 1;
        return acc;
      }, {});
      previousActivityStatusRef.current = { ...previousActivityStatusRef.current, ...updatedActivityStatus };
    }
  }, [data]);

  // Helper function to open confirmation modal
  const openConfirmationModal = (title, message, onConfirm, deviceUUIDs, actionType) => {
    setModalConfig({
      title,
      message,
      onConfirm,
      deviceUUIDs,
      actionType,
    });
    setIsModalOpen(true);
  };

  // Handler for inactive device click
  const handleInactiveDeviceClick = () => {
    setIsInactiveModalOpen(true);
  };

  // Handle USB block/unblock action for a single device
  const handleUSBAction = (device, currentStatus) => {
    const action = currentStatus === 'block' ? 'unblock' : 'block';
    const actionLabel = action === 'block' ? 'Block USB' : 'Unblock USB';
    const operationType = action === 'block' ? 'usb_blocking' : 'usb_unblock';
    openConfirmationModal(
      `Confirm ${actionLabel}`,
      `Are you sure you want to ${action} USB for this device?`,
      async () => {
        try {
          setLoadingDevices(prev => ({ ...prev, [device.uuid]: true }));

          let operationLogId;
          const check = await checkValid({
            licence_id: device.licence_id,
            operation: operationType
          })
          const res = check.data
          if (res.status === "expired") {
            toast.error("This license has expired.");
            return;
          }

          if (res.status === "not_allowed") {
            toast.error("Operation is not permitted for this license.");
            return;
          }

          if (res.status !== "allowed") {
            toast.error("Invalid license response.");
            return;
          }
          if (device.isActive === 1) {
            const operationResponse = await addOperation({
              device_id: device.id,
              type: operationType,
              user_id: auth.user,
            });
            operationLogId = operationResponse.data.log_id;
            const res = await blockUSB(device.uuid, action, operationLogId, device.sys_uuid);
            //console.log(res.status)
            if (res.status === 200) {
              const response = await getUSBstatus(device.uuid, device.sys_uuid);
              if (response.status === 200) {
                await editUsbStatus({
                  id: device.id,
                  status: response.data.status,
                });
                handleUpdate()
              }
            }
          } else {
            const queueResponse = await addQueueOperation({
              device_id: device.id,
              type: operationType,
              user_id: auth.user,
            });
            operationLogId = queueResponse.data.log_id;
          }
        } catch (error) {
          console.error(`Error performing ${action} USB for device ${device.uuid}:`, error);
        } finally {
          setLoadingDevices(prev => ({ ...prev, [device.uuid]: false }));
        }
      },
      [device.uuid],
      `usb-${action}`
    );
  };

  // Handle Erase w/o OS for a single device
  const handleEraseWithoutOS = (device) => {
    openConfirmationModal(
      'Confirm Wipe w/o OS',
      'Are you sure you want to wipe all non-OS drives for this device? This action cannot be undone.',
      async () => {
        try {
          setLoadingDevices(prev => ({ ...prev, [device.uuid]: true }));
          if (device.isActive === 1) {
            const operationResponse = await requestOperation({
              device_id: device.id,
              type: 'delete_drive',
              user_id: auth.user,
            });
          } else {
            const queueResponse = await requestOperation({
              device_id: device.id,
              type: 'delete_drive',
              user_id: auth.user,
            });
          }
        } catch (error) {
          console.error(`Error performing Erase w/o OS for device ${device.uuid}`, error);
        } finally {
          setLoadingDevices(prev => ({ ...prev, [device.uuid]: false }));
        }
      },
      [device.uuid],
      'wipe-without-os'
    );
  };

  // Handle Nuke OS for a single device (placeholder implementation)
  const handleNukeOS = (device) => {
    openConfirmationModal(
      'Confirm Nuke OS',
      'Are you sure you want to nuke the OS for this device? This action will erase all data, including the operating system, and cannot be undone.',
      async () => {
        try {
          setLoadingDevices(prev => ({ ...prev, [device.uuid]: true }));
          // Placeholder: Implement actual API call for Nuke OS
        } catch (error) {
          console.error(`Error performing Nuke OS for device ${device.uuid}`, error);
        } finally {
          setLoadingDevices(prev => ({ ...prev, [device.uuid]: false }));
        }
      },
      [device.uuid],
      'nuke-os'
    );
  };

  return (
    <div className="mb-5">
      <div className="flex justify-between items-center mb-4">
        <Typography variant="h6" sx={{ fontWeight: 700 }}>
          Devices
        </Typography>
        <div className="flex items-center">
          <Button
            size="sm"
            variant="filled"
            className="export-button"
            styles={{
              root: {
                width: '120px',
                height: '36px',
                backgroundColor: '#A855F7',
                borderRadius: '20px',
                padding: '10px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '6px',
                border: 'none',
                cursor: 'pointer',
                boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
                '&:hover': {
                  backgroundColor: '#A855A9',
                },
              },
              label: {
                fontFamily: 'Nunito Sans, sans-serif',
                fontSize: '14px',
                fontWeight: 500,
                color: '#fff',
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
              },
            }}
            onClick={(e) => {
              const button = e.currentTarget;
              const ripple = document.createElement('span');
              const diameter = Math.max(button.clientWidth, button.clientHeight);
              const radius = diameter / 2;

              ripple.style.width = ripple.style.height = `${diameter}px`;
              ripple.style.left = `${e.clientX - button.getBoundingClientRect().left - radius}px`;
              ripple.style.top = `${e.clientY - button.getBoundingClientRect().top - radius}px`;
              ripple.classList.add('export-button-ripple');

              button.appendChild(ripple);

              setTimeout(() => {
                ripple.remove();
              }, 600);

              exportToCSV(data, 'devices.csv');
            }}
          >
            <FontAwesomeIcon
              icon={faDownload}
              style={{
                height: '10px',
                width: '10px',
                color: '#fff',
              }}
            />
            <span>Export</span>
          </Button>
          <div style={{ width: '30px' }} />

        </div>
      </div>
      <Table
        columns={columns(auth, handleUSBAction, handleUnlicencedDeviceClick, handleEraseWithoutOS, handleNukeOS, loadingDevices, handleInactiveDeviceClick, handleOpenLocation)} data={data} />
      <ConformationDialog
        open={isModalOpen}
        title={modalConfig.title}
        message={modalConfig.message}
        onConfirm={() => {
          modalConfig.onConfirm();
          setIsModalOpen(false);
        }}
        onCancel={() => setIsModalOpen(false)}
        confirmLabel="Confirm"
        cancelLabel="Cancel"
      />
      {/* ---------------------- Inactive Device Dialog ---------------------- */}
      <Dialog
        open={isInactiveModalOpen}
        onClose={() => setIsInactiveModalOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 0,
            boxShadow: "6px 6px 6px #00000040",
            border: "none",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Nunito Sans, sans-serif",
            fontSize: "20px",
            fontWeight: 700,
            color: "#202224",
          }}
        >
          Device Inactive
        </DialogTitle>

        <DialogContent
          sx={{
            fontFamily: "Nunito Sans, sans-serif",
            fontSize: "16px",
            color: "#595C60",
          }}
        >
          <Typography>
            This device is currently inactive. You cannot access the drive details
            page for inactive devices. Please ensure the device is active to view
            its details.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setIsInactiveModalOpen(false)}
            sx={{
              backgroundColor: "#A855F7",
              borderRadius: "20px",
              padding: "10px 20px",
              fontFamily: "Nunito Sans, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              color: "#fff",
              textTransform: "none",
              boxShadow:
                "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
              "&:hover": { backgroundColor: "#A855F7" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>


      {/* ---------------------- Unlicensed Device Dialog ---------------------- */}
      <Dialog
        open={isUnlicencedModalOpen}
        onClose={() => setIsUnlicencedModalOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 0,
            boxShadow: "6px 6px 6px #00000040",
            border: "none",
            p: 1,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Nunito Sans, sans-serif",
            fontSize: "20px",
            fontWeight: 700,
            color: theme.palette.text.primary,
          }}
        >
          Device Not Licenced
        </DialogTitle>

        <DialogContent
          sx={{
            fontFamily: "Nunito Sans, sans-serif",
            fontSize: "16px",
            color: theme.palette.bg.default,
          }}
        >
          <Typography>
            This device is not licenced. You cannot access the drive details page for
            unlicensed devices. Please attach a licence to this device to view
            its details.
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setIsUnlicencedModalOpen(false)}
            sx={{
              backgroundColor: "#A855F7",
              borderRadius: "20px",
              padding: "10px 20px",
              fontFamily: "Nunito Sans, sans-serif",
              fontSize: "14px",
              fontWeight: 500,
              color: "#fff",
              textTransform: "none",
              boxShadow:
                "0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)",
              "&:hover": { backgroundColor: "#A855F7" },
            }}
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <ShowLocation
        open={locationOpen}
        onClose={handleCloseLocation}
        ip={locationIp}
      />

    </div>
  );
};

export default DeviceTable;